import customerRideApi from 'api/customerRideApi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatRideMoney, formatRideAddress, rideErrorMessage, rideStatusMeta } from '../rideUtils';
import RideBookingDetailModal from './RideBookingDetailModal';
import RideDemandDetailModal from './RideDemandDetailModal';
import RideBookingModal from './RideBookingModal';

const getItemDateKey = (item) => {
  const val = item?.departureAt || item?.departureFrom || item?.tripDepartureAt || item?.createdAt;
  if (!val) return 'UNKNOWN';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return 'UNKNOWN';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getItemTimestamp = (item) => {
  const val = item?.departureAt || item?.departureFrom || item?.tripDepartureAt || item?.createdAt;
  if (!val) return 0;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const getTodayDateKey = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateHeader = (dateKey) => {
  if (!dateKey || dateKey === 'UNKNOWN') return 'Thời gian khác';
  try {
    const [y, m, d] = dateKey.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(y, m - 1, d);
    compareDate.setHours(0, 0, 0, 0);

    const diffDays = Math.round((compareDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const dayNames = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
    const dayOfWeek = dayNames[dateObj.getDay()] || '';
    const dateFormatted = `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;

    if (diffDays === 0) return `Hôm nay • ${dayOfWeek}, ${dateFormatted}`;
    if (diffDays === -1) return `Hôm qua • ${dayOfWeek}, ${dateFormatted}`;
    if (diffDays === 1) return `Ngày mai • ${dayOfWeek}, ${dateFormatted}`;
    return `${dayOfWeek}, ${dateFormatted}`;
  } catch (_) {
    return dateKey;
  }
};

const formatDatePillLabel = (dateKey) => {
  if (!dateKey || dateKey === 'UNKNOWN') return 'Khác';
  try {
    const [, m, d] = dateKey.split('-');
    return `${d}/${m}`;
  } catch (_) {
    return dateKey;
  }
};

const addDaysDateKey = (baseKey, days) => {
  const [year, month, day] = baseKey.split('-').map(Number);
  const value = new Date(year, month - 1, day);
  value.setDate(value.getDate() + days);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
};

const formatTimeOnly = (value) => {
  if (!value) return '--:--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatDateShort = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Creating a BIDV QR only starts a payment attempt; it is not yet a ride.
// Keep abandoned/expired attempts out of the customer's trip history while
// preserving cash rides and BIDV rides that were paid (or later refunded).
export const isUnpaidBidvRide = (item) => {
  if (String(item?.paymentMethod || '').toUpperCase() !== 'BIDV') return false;
  const paymentStatus = String(item?.paymentStatus || '').toUpperCase();
  const status = String(item?.status || '').toUpperCase();
  return ['PENDING', 'FAILED', 'NOT_STARTED'].includes(paymentStatus)
    || ['AWAITING_PAYMENT', 'PAYMENT_EXPIRED'].includes(status);
};

const MyRideBookings = ({ refreshToken = 0, initialSection = 'BOOKED', onFindTrips }) => {
  const [section, setSection] = useState(initialSection); // 'BOOKED' | 'MINE' | 'DRIVER'
  const [filter, setFilter] = useState('UPCOMING'); // 'UPCOMING' | 'ONGOING' | 'COMPLETED' | 'CANCELLED'
  const [selectedDate, setSelectedDate] = useState(initialSection === 'DRIVER' ? getTodayDateKey() : ''); // 'YYYY-MM-DD'
  const [datePreset, setDatePreset] = useState('ALL'); // 'ALL' | 'TODAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'CUSTOM'
  const [showFilterBar, setShowFilterBar] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [demands, setDemands] = useState([]);
  const [driverTrips, setDriverTrips] = useState([]);
  const [driverTripsError, setDriverTripsError] = useState('');
  const [demandsError, setDemandsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDemand, setSelectedDemand] = useState(null);
  const [selectedDriverTrip, setSelectedDriverTrip] = useState(null);
  const loadInFlightRef = useRef(false);

  const load = async ({ silent = false } = {}) => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const bookingsRequest = (async () => {
        try {
          const response = await customerRideApi.getMyBookings();
          setBookings(response.bookings || []);
          setError('');
        } catch (requestError) {
          if (!silent) setError(rideErrorMessage(requestError));
        }
      })();

      // These three tabs have independent APIs. A booking-history outage must
      // not prevent customers from seeing marketplace trips or demands.
      const marketplaceRequest = typeof customerRideApi.browseTrips === 'function'
        ? (async () => {
          try {
            const tripResponse = await customerRideApi.browseTrips({ page: 1, pageSize: 100 });
            setDriverTrips(tripResponse?.items || tripResponse?.trips || []);
            setDriverTripsError('');
          } catch (requestError) {
            setDriverTripsError(`Không tải được danh sách chuyến tài xế đăng. ${rideErrorMessage(requestError)}`);
          }
        })()
        : Promise.resolve();

      const demandsRequest = typeof customerRideApi.getMyScheduledDemands === 'function'
        ? (async () => {
        try {
          const demandResponse = await customerRideApi.getMyScheduledDemands();
          setDemands(demandResponse?.demands || []);
          setDemandsError('');
        } catch (requestError) {
          setDemandsError(`Không tải được danh sách yêu cầu. ${rideErrorMessage(requestError)}`);
        }
        })()
        : Promise.resolve();

      await Promise.all([bookingsRequest, marketplaceRequest, demandsRequest]);
    } finally {
      loadInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshToken]);

  // Keep the list live even when it is currently empty. The previous
  // conditional poll stopped exactly when a new trip was created elsewhere,
  // so this tab stayed empty until a manual refresh or navigation.
  useEffect(() => {
    const refreshLiveData = () => {
      if (document.visibilityState === 'hidden') return;
      load({ silent: true });
    };
    const timer = window.setInterval(refreshLiveData, 5000);
    window.addEventListener('focus', refreshLiveData);
    document.addEventListener('visibilitychange', refreshLiveData);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshLiveData);
      document.removeEventListener('visibilitychange', refreshLiveData);
    };
  }, [refreshToken]);

  const visibleBookings = useMemo(
    () => bookings.filter((booking) => !isUnpaidBidvRide(booking)),
    [bookings],
  );
  const visibleDemands = useMemo(
    () => demands.filter((demand) => !isUnpaidBidvRide(demand)),
    [demands],
  );

  // Status-filtered bookings
  const filtered = useMemo(() => visibleBookings.filter((booking) => {
    if (filter === 'UPCOMING') {
      return (
        ['PENDING_DRIVER', 'CONFIRMED'].includes(booking.status) &&
        !['BOARDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(booking.tripStatus)
      );
    }
    if (filter === 'ONGOING') {
      return (
        booking.status === 'CONFIRMED' &&
        ['BOARDING', 'IN_PROGRESS'].includes(booking.tripStatus)
      );
    }
    if (filter === 'COMPLETED') {
      return booking.status === 'CONFIRMED' && booking.tripStatus === 'COMPLETED';
    }
    // CANCELLED tab
    return (
      ['REJECTED', 'CANCELLED', 'EXPIRED', 'PICKUP_FAILED'].includes(booking.status) ||
      ['CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(booking.tripStatus)
    );
  }), [visibleBookings, filter]);

  // Status-filtered demands
  const filteredDemands = useMemo(() => visibleDemands.filter((demand) => {
    const status = String(demand.status || '').toUpperCase();
    if (filter === 'UPCOMING') {
      return ['OPEN', 'MATCHED'].includes(status);
    }
    if (filter === 'ONGOING') {
      return status === 'MATCHED' && demand.departureFrom
        && new Date(demand.departureFrom).getTime() <= Date.now()
        && (!demand.departureTo || new Date(demand.departureTo).getTime() >= Date.now());
    }
    if (filter === 'COMPLETED') return ['COMPLETED', 'FINISHED'].includes(status);
    return ['CANCELLED', 'EXPIRED', 'PAYMENT_EXPIRED'].includes(status);
  }), [visibleDemands, filter]);

  // The mobile app's "Tài xế đăng" view is date-based.  It does not reuse
  // the passenger status tabs, so keep every driver trip here and let the
  // date chips below do the filtering.
  const filteredDriverTrips = useMemo(() => driverTrips, [driverTrips]);

  // Overall counts for tabs
  const bookingCounts = useMemo(() => {
    const counts = { UPCOMING: 0, ONGOING: 0, COMPLETED: 0, CANCELLED: 0 };
    for (const b of visibleBookings) {
      if (['PENDING_DRIVER', 'CONFIRMED'].includes(b.status) && !['BOARDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(b.tripStatus)) {
        counts.UPCOMING += 1;
      } else if (b.status === 'CONFIRMED' && ['BOARDING', 'IN_PROGRESS'].includes(b.tripStatus)) {
        counts.ONGOING += 1;
      } else if (b.status === 'CONFIRMED' && b.tripStatus === 'COMPLETED') {
        counts.COMPLETED += 1;
      } else if (['REJECTED', 'CANCELLED', 'EXPIRED', 'PICKUP_FAILED'].includes(b.status) || ['CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(b.tripStatus)) {
        counts.CANCELLED += 1;
      }
    }
    return counts;
  }, [visibleBookings]);

  const demandCounts = useMemo(() => {
    const counts = { UPCOMING: 0, ONGOING: 0, COMPLETED: 0, CANCELLED: 0 };
    for (const d of visibleDemands) {
      const st = String(d.status || '').toUpperCase();
      if (['OPEN', 'MATCHED'].includes(st)) {
        counts.UPCOMING += 1;
      } else if (st === 'MATCHED' && d.departureFrom && new Date(d.departureFrom).getTime() <= Date.now() && (!d.departureTo || new Date(d.departureTo).getTime() >= Date.now())) {
        counts.ONGOING += 1;
      } else if (['COMPLETED', 'FINISHED'].includes(st)) {
        counts.COMPLETED += 1;
      } else {
        counts.CANCELLED += 1;
      }
    }
    return counts;
  }, [visibleDemands]);

  const driverTripCounts = useMemo(() => {
    const counts = { UPCOMING: 0, ONGOING: 0, COMPLETED: 0, CANCELLED: 0 };
    for (const trip of driverTrips) {
      const status = String(trip.status || '').toUpperCase();
      if (status === 'PUBLISHED') counts.UPCOMING += 1;
      else if (['BOARDING', 'IN_PROGRESS'].includes(status)) counts.ONGOING += 1;
      else if (status === 'COMPLETED') counts.COMPLETED += 1;
      else if (['CANCELLED', 'EXPIRED', 'DRIVER_NO_SHOW'].includes(status)) counts.CANCELLED += 1;
    }
    return counts;
  }, [driverTrips]);

  const currentCounts = section === 'MINE' ? demandCounts : section === 'DRIVER' ? driverTripCounts : bookingCounts;
  const currentActiveList = section === 'MINE' ? filteredDemands : section === 'DRIVER' ? filteredDriverTrips : filtered;

  // Available unique dates in currently active status tab
  const availableDatesWithCounts = useMemo(() => {
    const countsMap = new Map();
    for (const item of currentActiveList) {
      const k = getItemDateKey(item);
      if (k && k !== 'UNKNOWN') {
        countsMap.set(k, (countsMap.get(k) || 0) + 1);
      }
    }
    return Array.from(countsMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [currentActiveList]);

  const driverDateOptions = useMemo(() => {
    const today = getTodayDateKey();
    // Keep this in sync with the app's DemandDateFilter (7 quick dates,
    // starting today; the row can scroll horizontally on smaller screens).
    return Array.from({ length: 7 }, (_, offset) => ({
      key: addDaysDateKey(today, offset),
      label: offset === 0 ? 'Hôm nay' : offset === 1 ? 'Ngày mai' : formatDatePillLabel(addDaysDateKey(today, offset)),
    }));
  }, []);

  // Date-filtered items
  const dateFilteredList = useMemo(() => {
    if (!selectedDate && datePreset === 'ALL') return currentActiveList;

    return currentActiveList.filter((item) => {
      const key = getItemDateKey(item);
      const ts = getItemTimestamp(item);

      if (selectedDate) {
        return key === selectedDate;
      }

      if (datePreset === 'TODAY') {
        return key === getTodayDateKey();
      }

      if (datePreset === 'THIS_WEEK') {
        if (!ts) return false;
        const diffMs = Math.abs(ts - Date.now());
        return diffMs <= 7 * 24 * 60 * 60 * 1000;
      }

      if (datePreset === 'THIS_MONTH') {
        if (!ts) return false;
        const d = new Date(ts);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }

      return true;
    });
  }, [currentActiveList, selectedDate, datePreset]);

  // Driver marketplace follows the app's soonest-first order; history stays newest-first.
  const groupedItems = useMemo(() => {
    const map = new Map();
    const sorted = [...dateFilteredList].sort((a, b) => (
      section === 'DRIVER'
        ? getItemTimestamp(a) - getItemTimestamp(b)
        : getItemTimestamp(b) - getItemTimestamp(a)
    ));
    for (const item of sorted) {
      const key = getItemDateKey(item);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return Array.from(map.entries());
  }, [dateFilteredList, section]);

  const demandStatusMeta = (status) => ({
    OPEN: { label: 'Đang tìm tài xế', tone: 'warning' },
    AWAITING_PAYMENT: { label: 'Chờ thanh toán', tone: 'warning' },
    MATCHED: { label: 'Đã ghép tài xế', tone: 'success' },
    COMPLETED: { label: 'Đã hoàn thành', tone: 'success' },
    FINISHED: { label: 'Đã hoàn thành', tone: 'success' },
    CANCELLED: { label: 'Đã hủy', tone: 'neutral' },
    EXPIRED: { label: 'Đã hết hạn', tone: 'neutral' },
    PAYMENT_EXPIRED: { label: 'Hết hạn thanh toán', tone: 'neutral' },
  }[String(status || '').toUpperCase()] || { label: status || 'Yêu cầu hẹn giờ', tone: 'neutral' });

  const driverTripStatusMeta = (status) => ({
    PUBLISHED: { label: 'Sắp tới', tone: 'success' },
    BOARDING: { label: 'Đang diễn ra', tone: 'info' },
    IN_PROGRESS: { label: 'Đang diễn ra', tone: 'info' },
    COMPLETED: { label: 'Đã hoàn thành', tone: 'success' },
    CANCELLED: { label: 'Đã hủy', tone: 'neutral' },
    EXPIRED: { label: 'Đã hết hạn', tone: 'neutral' },
    DRIVER_NO_SHOW: { label: 'Không thực hiện', tone: 'danger' },
  }[String(status || '').toUpperCase()] || { label: status || 'Chuyến đăng', tone: 'neutral' });

  const getEmptyTitle = () => {
    const names = { BOOKED: 'chuyến đi', MINE: 'yêu cầu', DRIVER: 'chuyến tài xế đăng' };
    const states = { UPCOMING: 'sắp tới', ONGOING: 'đang diễn ra', COMPLETED: 'đã hoàn thành', CANCELLED: 'đã hủy' };
    return `Bạn chưa có ${names[section] || 'chuyến đi'} nào ${states[filter] || 'sắp tới'}`;
  };

  const getEmptyText = () => {
    if (section === 'BOOKED') return 'Tìm chuyến phù hợp để bắt đầu hành trình.';
    if (section === 'MINE') return 'Các yêu cầu của bạn sẽ xuất hiện tại đây.';
    return 'Các chuyến do tài xế đăng sẽ xuất hiện tại đây.';
  };

  const renderDemandCard = (demand) => {
    const meta = demandStatusMeta(demand.status);
    const pickup = formatRideAddress(demand.pickup?.address);
    const dropoff = formatRideAddress(demand.dropoff?.address);
    const fare = demand.fare;
    const payable = Math.max(0, Number(fare?.totalFare || 0) - Number(fare?.promoDiscountAmount || 0));
    const depTime = demand.departureFrom;

    return (
      <div
        key={demand.demandId}
        className="customer-ride-booking-row demand-card"
        onClick={() => setSelectedDemand(demand)}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedDemand(demand); }}
        role="button"
        tabIndex={0}
      >
        <div className="booking-row-top">
          <div className="booking-top-left">
            <span className="ride-card-type-chip scheduled">
              <i className="fa-regular fa-clock" /> Chuyến hẹn giờ
            </span>
            <div className="booking-time-badge">
              <i className="fa-regular fa-clock" />
              <strong className="time-highlight">{formatTimeOnly(depTime)}</strong>
              <span className="time-date-sub">{formatDateShort(depTime)}</span>
            </div>
          </div>
          <span className={`customer-ride-status ${meta.tone}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>

        <div className="booking-row-route">
          <div className="route-point start">
            <i className="fa-solid fa-circle start-dot" />
            <div className="route-point-text">
              <strong className="point-primary">{pickup.primary}</strong>
              {pickup.secondary && <span className="point-secondary">{pickup.secondary}</span>}
            </div>
          </div>
          <div className="route-vertical-line" />
          <div className="route-point end">
            <i className="fa-solid fa-location-dot end-pin" />
            <div className="route-point-text">
              <strong className="point-primary">{dropoff.primary}</strong>
              {dropoff.secondary && <span className="point-secondary">{dropoff.secondary}</span>}
            </div>
          </div>
        </div>

        <div className="booking-row-bottom">
          <div className="booking-meta-info">
            <span className="meta-tag">
              <i className="fa-regular fa-user" /> {demand.seats || 1} ghế
            </span>
            {fare ? (
              <strong className="meta-price">
                {formatRideMoney(payable, fare.currency)}
              </strong>
            ) : (
              <span className="meta-tag-id">
                Mã: {demand.demandId.slice(0, 8)}...
              </span>
            )}
          </div>
          <span className="detail-action-link">
            Chi tiết <i className="fa-solid fa-chevron-right" />
          </span>
        </div>
      </div>
    );
  };

  const renderBookingCard = (booking) => {
    const meta = booking.paymentMethod === 'BIDV' && booking.paymentStatus === 'PENDING'
      ? { label: 'Chờ thanh toán', tone: 'warning' }
      : rideStatusMeta(booking.status);
    const pickup = formatRideAddress(booking.pickup?.address);
    const dropoff = formatRideAddress(booking.dropoff?.address);
    const payable = Math.max(0, Number(booking.totalFare || 0) - Number(booking.promoDiscountAmount || 0));
    const depTime = booking.tripDepartureAt;

    return (
      <div
        key={booking.bookingId}
        className="customer-ride-booking-row booked-card"
        onClick={() => setSelectedId(booking.bookingId)}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedId(booking.bookingId); }}
        role="button"
        tabIndex={0}
      >
        <div className="booking-row-top">
          <div className="booking-top-left">
            <span className="ride-card-type-chip normal">
              <i className="fa-solid fa-car-side" /> Xe ghép EVG
            </span>
            <div className="booking-time-badge">
              <i className="fa-regular fa-clock" />
              <strong className="time-highlight">{formatTimeOnly(depTime)}</strong>
              <span className="time-date-sub">{formatDateShort(depTime)}</span>
            </div>
          </div>
          <span className={`customer-ride-status ${meta.tone}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>

        <div className="booking-row-route">
          <div className="route-point start">
            <i className="fa-solid fa-circle start-dot" />
            <div className="route-point-text">
              <strong className="point-primary">{pickup.primary}</strong>
              {pickup.secondary && <span className="point-secondary">{pickup.secondary}</span>}
            </div>
          </div>
          <div className="route-vertical-line" />
          <div className="route-point end">
            <i className="fa-solid fa-location-dot end-pin" />
            <div className="route-point-text">
              <strong className="point-primary">{dropoff.primary}</strong>
              {dropoff.secondary && <span className="point-secondary">{dropoff.secondary}</span>}
            </div>
          </div>
        </div>

        <div className="booking-row-bottom">
          <div className="booking-meta-info">
            <span className="meta-tag">
              <i className="fa-regular fa-user" /> {booking.seats} ghế
            </span>
            <strong className="meta-price">
              {formatRideMoney(payable, booking.currency)}
            </strong>
          </div>
          <span className="detail-action-link">
            Chi tiết <i className="fa-solid fa-chevron-right" />
          </span>
        </div>
      </div>
    );
  };

  const renderDriverTripCard = (trip) => {
    const meta = driverTripStatusMeta(trip.status);
    const pickup = formatRideAddress(trip.origin?.address || trip.originAddress);
    const dropoff = formatRideAddress(trip.destination?.address || trip.destinationAddress);
    const depTime = trip.departureAt;
    const availableSeats = trip.seatsAvailable == null
      ? Math.max(0, Number(trip.seatsOffered || 0) - Number(trip.seatsConfirmed || 0))
      : Math.max(0, Number(trip.seatsAvailable));
    const seatsTotal = trip.seatsOffered == null ? null : Number(trip.seatsOffered);
    const priceValue = trip.fullRoute?.farePerSeat ?? trip.pricePerSeat;
    const price = priceValue == null ? null : formatRideMoney(priceValue, trip.currency);
    const canBook = String(trip.status || '').toUpperCase() === 'PUBLISHED'
      && availableSeats > 0
      && getItemTimestamp(trip) > Date.now();
    const openBooking = () => {
      if (!canBook) return;
      setSelectedDriverTrip({
        trip,
        searchPayload: { pickup: trip.origin, dropoff: trip.destination, seats: 1, isCharter: false },
      });
    };
    return (
      <div
        key={trip.tripId}
        className={`customer-ride-booking-row driver-trip-card${canBook ? '' : ' is-unavailable'}`}
        onClick={openBooking}
        onKeyDown={(event) => {
          if (canBook && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            openBooking();
          }
        }}
        role={canBook ? 'button' : undefined}
        tabIndex={canBook ? 0 : undefined}
        aria-disabled={canBook ? undefined : true}
      >
        <div className="booking-row-top">
          <div className="booking-top-left">
            <span className="ride-card-type-chip driver">
              <i className="fa-solid fa-car-side" /> Tài xế đăng
            </span>
            <div className="booking-time-badge">
              <i className="fa-regular fa-clock" />
              <strong className="time-highlight">{formatTimeOnly(depTime)}</strong>
              <span className="time-date-sub">{formatDateShort(depTime)}</span>
            </div>
          </div>
          <span className={`customer-ride-status ${meta.tone}`}>
            <span className="status-dot" /> {meta.label}
          </span>
        </div>

        <div className="booking-row-route">
          <div className="route-point start">
            <i className="fa-solid fa-circle start-dot" />
            <div className="route-point-text">
              <strong className="point-primary">{pickup.primary}</strong>
              {pickup.secondary && <span className="point-secondary">{pickup.secondary}</span>}
            </div>
          </div>
          <div className="route-vertical-line" />
          <div className="route-point end">
            <i className="fa-solid fa-location-dot end-pin" />
            <div className="route-point-text">
              <strong className="point-primary">{dropoff.primary}</strong>
              {dropoff.secondary && <span className="point-secondary">{dropoff.secondary}</span>}
            </div>
          </div>
        </div>

        <div className="booking-row-bottom">
          <div className="booking-meta-info">
            <span className="meta-tag"><i className="fa-regular fa-user" /> {seatsTotal ? `${availableSeats}/${seatsTotal} ghế trống` : `${availableSeats} ghế trống`}</span>
            {price && <strong className="meta-price">{price}<small> / ghế</small></strong>}
          </div>
          <span className="detail-action-link">{canBook ? <><span>Xem và đặt</span> <i className="fa-solid fa-chevron-right" /></> : 'Không còn nhận đặt'}</span>
        </div>
        <div className="driver-trip-code">Mã chuyến: {trip.tripCode}</div>
      </div>
    );
  };

  return (
    <section id="trips" className="section active">
      {/* Page Header Card matching prototype */}
      <div className="page-header">
        <div>
          <h1>Chuyến đi</h1>
          <p>Theo dõi chuyến đã đặt, yêu cầu của bạn và chuyến do tài xế đăng.</p>
        </div>
        <button
          type="button"
          className={`outline ${showFilterBar ? 'active' : ''}`}
          onClick={() => setShowFilterBar((prev) => !prev)}
        >
          <i className="fa-regular fa-sliders" /> {showFilterBar ? 'Ẩn bộ lọc ngày' : 'Lọc ngày'}
        </button>
      </div>

      {/* Panel Trip Panel matching prototype */}
      <div className="panel trip-panel">
        {/* Tier 1 Trip Tabs */}
        <div className="trip-tabs">
          <button
            type="button"
            className={section === 'BOOKED' ? 'active' : ''}
            onClick={() => {
              setSection('BOOKED');
              setSelectedDate('');
              setDatePreset('ALL');
            }}
          >
            Đã đặt {bookingCounts.UPCOMING + bookingCounts.ONGOING + bookingCounts.COMPLETED + bookingCounts.CANCELLED > 0 && (
              <span className="tab-total-pill">{bookingCounts.UPCOMING + bookingCounts.ONGOING + bookingCounts.COMPLETED + bookingCounts.CANCELLED}</span>
            )}
          </button>
          <button
            type="button"
            className={section === 'MINE' ? 'active' : ''}
            onClick={() => {
              setSection('MINE');
              setSelectedDate('');
              setDatePreset('ALL');
            }}
          >
            Yêu cầu của tôi {demandCounts.UPCOMING + demandCounts.ONGOING + demandCounts.COMPLETED + demandCounts.CANCELLED > 0 && (
              <span className="tab-total-pill">{demandCounts.UPCOMING + demandCounts.ONGOING + demandCounts.COMPLETED + demandCounts.CANCELLED}</span>
            )}
          </button>
          <button
            type="button"
            className={section === 'DRIVER' ? 'active' : ''}
            onClick={() => {
              setSection('DRIVER');
              setSelectedDate(getTodayDateKey());
              setDatePreset('CUSTOM');
            }}
          >
            Tài xế đăng
          </button>
        </div>

        {/* Passenger/request status filters. The driver-posted view is
            intentionally date-only, matching the mobile app. */}
        {section !== 'DRIVER' && <div className="filterbar" id="tripFilters">
          <button
            type="button"
            className={filter === 'UPCOMING' ? 'active' : ''}
            onClick={() => {
              setFilter('UPCOMING');
              setSelectedDate('');
              setDatePreset('ALL');
            }}
          >
            Sắp tới {currentCounts.UPCOMING > 0 && <span className="filter-count-badge">{currentCounts.UPCOMING}</span>}
          </button>
          <button
            type="button"
            className={filter === 'ONGOING' ? 'active' : ''}
            onClick={() => {
              setFilter('ONGOING');
              setSelectedDate('');
              setDatePreset('ALL');
            }}
          >
            Đang diễn ra {currentCounts.ONGOING > 0 && <span className="filter-count-badge">{currentCounts.ONGOING}</span>}
          </button>
          <button
            type="button"
            className={filter === 'COMPLETED' ? 'active' : ''}
            onClick={() => {
              setFilter('COMPLETED');
              setSelectedDate('');
              setDatePreset('ALL');
            }}
          >
            Đã hoàn thành {currentCounts.COMPLETED > 0 && <span className="filter-count-badge">{currentCounts.COMPLETED}</span>}
          </button>
          <button
            type="button"
            className={filter === 'CANCELLED' ? 'active' : ''}
            onClick={() => {
              setFilter('CANCELLED');
              setSelectedDate('');
              setDatePreset('ALL');
            }}
          >
            Đã hủy {currentCounts.CANCELLED > 0 && <span className="filter-count-badge">{currentCounts.CANCELLED}</span>}
          </button>
        </div>}

        {/* Date Filter Bar */}
        {section === 'DRIVER' && showFilterBar && (
          <div className="driver-date-tabs" aria-label="Lọc ngày chuyến tài xế đăng">
            {driverDateOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={selectedDate === option.key ? 'active' : ''}
                onClick={() => {
                  setSelectedDate(option.key);
                  setDatePreset('CUSTOM');
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {section !== 'DRIVER' && showFilterBar && currentActiveList.length > 0 && (
          <div className="ride-date-filter-bar">
            <div className="date-filter-header">
              <span className="date-filter-label">
                <i className="fa-regular fa-calendar-days" /> Lọc theo ngày:
              </span>

              <div className={`date-filter-picker-wrap ${selectedDate ? 'has-date' : ''}`}>
                <i className="fa-regular fa-calendar-days date-picker-icon" />
                <span className="date-picker-display">
                  {selectedDate ? formatDateShort(selectedDate) : 'Chọn ngày'}
                </span>
                <input
                  type="date"
                  className="date-filter-input"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setDatePreset('CUSTOM');
                  }}
                  title="Chọn ngày cụ thể từ lịch"
                />
                {selectedDate ? (
                  <button
                    type="button"
                    className="clear-date-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDate('');
                      setDatePreset('ALL');
                    }}
                    title="Xoá lọc ngày"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                ) : (
                  <i className="fa-solid fa-chevron-down date-dropdown-arrow" />
                )}
              </div>
            </div>

            <div className="date-filter-presets">
              <button
                type="button"
                className={!selectedDate && datePreset === 'ALL' ? 'active' : ''}
                onClick={() => {
                  setSelectedDate('');
                  setDatePreset('ALL');
                }}
              >
                Tất cả ({currentActiveList.length})
              </button>

              {/* Specific Dates from current trips */}
              {availableDatesWithCounts.slice(0, 6).map(([dKey, cnt]) => (
                <button
                  key={dKey}
                  type="button"
                  className={selectedDate === dKey ? 'active' : ''}
                  onClick={() => {
                    setSelectedDate(dKey);
                    setDatePreset('CUSTOM');
                  }}
                  title={formatDateHeader(dKey)}
                >
                  {formatDatePillLabel(dKey)} <span className="pill-cnt">({cnt})</span>
                </button>
              ))}

              {/* Additional presets if multiple dates */}
              {availableDatesWithCounts.length > 3 && (
                <>
                  <button
                    type="button"
                    className={!selectedDate && datePreset === 'THIS_WEEK' ? 'active' : ''}
                    onClick={() => {
                      setSelectedDate('');
                      setDatePreset('THIS_WEEK');
                    }}
                  >
                    7 ngày gần đây
                  </button>
                  <button
                    type="button"
                    className={!selectedDate && datePreset === 'THIS_MONTH' ? 'active' : ''}
                    onClick={() => {
                      setSelectedDate('');
                      setDatePreset('THIS_MONTH');
                    }}
                  >
                    Tháng này
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Active filter notification banner */}
        {selectedDate && currentActiveList.length > 0 && (
          <div className="active-date-filter-banner">
            <div className="banner-left">
              <i className="fa-solid fa-calendar-check" />
              <span>
                Đang lọc chuyến ngày: <strong>{formatDateHeader(selectedDate)}</strong> ({dateFilteredList.length} chuyến)
              </span>
            </div>
            <button
              type="button"
              className="banner-clear-btn"
              onClick={() => {
                setSelectedDate('');
                setDatePreset('ALL');
              }}
            >
              <i className="fa-solid fa-xmark" /> Xem tất cả các ngày
            </button>
          </div>
        )}

        {/* Content Area */}
        {loading ? (
          <div className="customer-ride-loading">
            <i className="fa-solid fa-spinner fa-spin" /> Đang tải chuyến của bạn...
          </div>
        ) : section === 'BOOKED' && error ? (
          <div className="customer-ride-alert danger">
            {error}
            <button type="button" onClick={() => load()}>Thử lại</button>
          </div>
        ) : section === 'MINE' && demandsError ? (
          <div className="customer-ride-alert danger">
            {demandsError}
            <button type="button" onClick={() => load()}>Thử lại</button>
          </div>
        ) : section === 'DRIVER' && driverTripsError && driverTrips.length === 0 ? (
          <div className="customer-ride-alert danger">
            {driverTripsError}
            <button type="button" onClick={() => load()}>Thử lại</button>
          </div>
        ) : currentActiveList.length === 0 ? (
          <div className="empty">
            <div className="emptycircle">
              <i className="fa-regular fa-calendar" />
            </div>
            <h3 id={section === 'MINE' ? 'emptyDemandTitle' : 'emptyTitle'}>{getEmptyTitle()}</h3>
            <p id={section === 'MINE' ? 'emptyDemandText' : 'emptyText'}>{getEmptyText()}</p>
            {onFindTrips && (
              <button
                id="findTripBtn"
                type="button"
                className="btn"
                onClick={onFindTrips}
              >
                <i className="fa-regular fa-magnifying-glass" /> Tìm chuyến
              </button>
            )}
          </div>
        ) : dateFilteredList.length === 0 ? (
          <div className="empty">
            <div className="emptycircle">
              <i className="fa-regular fa-calendar-xmark" />
            </div>
            <h3>Không có {section === 'MINE' ? 'yêu cầu' : 'chuyến đi'} nào trong ngày đã chọn</h3>
            <p>Vui lòng chọn ngày khác trên thanh lọc hoặc bấm bên dưới để xem lại tất cả.</p>
            <button
              type="button"
              className="btn secondary"
              onClick={() => {
                setSelectedDate('');
                setDatePreset('ALL');
              }}
            >
              <i className="fa-regular fa-rotate-left" /> Xem tất cả các ngày ({currentActiveList.length})
            </button>
          </div>
        ) : (
          <div className="customer-ride-grouped-container">
            {groupedItems.map(([dateKey, items]) => (
              <div key={dateKey} className="customer-ride-date-group">
                <div className="customer-ride-date-header">
                  <div className="date-header-left">
                    <span className="date-calendar-badge">
                      <i className="fa-regular fa-calendar-days" />
                    </span>
                    <h4 className="date-header-title">{formatDateHeader(dateKey)}</h4>
                  </div>
                  <span className="date-header-count">
                    {items.length} {section === 'MINE' ? 'yêu cầu' : 'chuyến'}
                  </span>
                </div>

                <div className="customer-ride-booking-list">
                  {items.map(section === 'MINE' ? renderDemandCard : section === 'DRIVER' ? renderDriverTripCard : renderBookingCard)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedId && (
        <RideBookingDetailModal
          bookingId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => load({ silent: true })}
        />
      )}
      {selectedDemand && !isUnpaidBidvRide(selectedDemand) && (
        <RideDemandDetailModal
          demand={selectedDemand}
          onClose={() => setSelectedDemand(null)}
          onChanged={() => {
            setSelectedDemand(null);
            load({ silent: true });
          }}
        />
      )}
      {selectedDriverTrip && (
        <RideBookingModal
          trip={selectedDriverTrip.trip}
          searchPayload={selectedDriverTrip.searchPayload}
          maxSeats={selectedDriverTrip.trip.seatsAvailable}
          onClose={() => setSelectedDriverTrip(null)}
          onBooked={(booking) => {
            if (booking?.bookingId) {
              setBookings((current) => [booking, ...current.filter((item) => item.bookingId !== booking.bookingId)]);
            }
            setSelectedDriverTrip(null);
            if (booking?.bookingId) setSelectedId(booking.bookingId);
            setSection('BOOKED');
            setFilter('UPCOMING');
            setSelectedDate('');
            setDatePreset('ALL');
            load({ silent: true });
          }}
        />
      )}
    </section>
  );
};

export default MyRideBookings;
